import React, { useState } from 'react';
import { Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSnackbar } from 'notistack';
import { exportXlsx, exportFilteredCsv } from './exportHelpers';

/**
 * Two flavours, picked at the call-site:
 *
 *   variant="csv"   → server-side CSV. POSTs to /appointment_delivery/export
 *                     with the same {@code andfilter / orfilter} body the
 *                     grid uses, plus any panel-specific extra filter
 *                     (pill / section). CSV mirrors V2 grid exactly.
 *
 *   variant="xlsx"  → FE-only multi-sheet xlsx from rows already in memory
 *                     (analytics aggregates, ABD party master). Zero extra
 *                     API calls.
 */
export default function ExportButton({
    variant = 'csv',
    label = 'Export',
    filters,
    extraAndFilter,
    extraOrFilter,
    fileName,
    sheets,
    color = 'primary',
    size = 'small',
    sx,
    // UI variant — outlined (default) vs contained ("solid colored" buttons in mockup).
    // bgColor + textColor lets caller match the exact mockup palette without
    // wrestling with MUI theme colors.
    buttonStyle = 'outlined',
    bgColor,
    textColor = '#fff',
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);

    const onClick = async () => {
        if (variant === 'xlsx') {
            try {
                exportXlsx(fileName || 'Export', sheets || []);
                enqueueSnackbar('Export ready', { variant: 'success' });
            } catch (e) {
                enqueueSnackbar(e?.message || 'Export failed', { variant: 'error' });
            }
            return;
        }
        // server-side csv
        setLoading(true);
        try {
            await exportFilteredCsv({
                filters,
                extraAndFilter,
                extraOrFilter,
                fileName,
                enqueueSnackbar,
            });
        } catch (e) {
            enqueueSnackbar(e?.message || 'Export failed', { variant: 'error' });
        }
        setLoading(false);
    };

    // Build sx merging base (contained color overrides) + caller sx.
    const containedSx = bgColor
        ? {
              bgcolor: bgColor,
              color: textColor,
              '&:hover': { bgcolor: bgColor, opacity: 0.9 },
              ...(sx || {}),
          }
        : sx;

    if (variant === 'csv') {
        return (
            <LoadingButton
                loading={loading}
                variant={buttonStyle}
                size={size}
                color={color}
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={onClick}
                sx={containedSx}
            >{label}</LoadingButton>
        );
    }
    return (
        <Button
            variant={buttonStyle}
            size={size}
            color={color}
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={onClick}
            sx={containedSx}
        >{label}</Button>
    );
}
